import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Counter = ({ target, isPercent, shouldStart }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    let start = 0;
    const end = parseInt(target);

    if (start === end) return;

    const duration = 1600;
    const incrementTime = 30;
    const step = Math.max(1, Math.ceil(end / (duration / incrementTime)));

    const timer = setInterval(() => {
      start += step;

      if (start >= end) {
        start = end;
        clearInterval(timer);
      }

      setCount(start);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [target, shouldStart]);

  return (
    <p className="mb-1 text-2xl font-bold tabular-nums text-slate-900 sm:text-3xl">
      {count}
      {isPercent ? '%' : '+'}
    </p>
  );
};

const AboutUs = () => {
  const { t } = useTranslation();
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, {
    once: true,
    margin: '-100px',
  });

  // Dynamic typed text — same effect as the Hero, rotating through what VizCorn does
  const [typedText, setTypedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const capabilities = t('hero.capabilities', { returnObjects: true });

  // Blue-family gradients to stay on-brand with the rest of the site
  const gradients = [
    'from-blue-600 to-indigo-600',
    'from-sky-500 to-blue-600',
    'from-indigo-500 to-blue-500',
    'from-blue-500 to-cyan-500',
    'from-blue-600 to-sky-500',
    'from-indigo-600 to-blue-600',
  ];

  useEffect(() => {
    const current = capabilities[currentServiceIndex];
    const typingSpeed = isDeleting ? 30 : 100;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        setTypedText(current.substring(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);

        if (currentIndex + 1 === current.length) {
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        setTypedText(current.substring(0, currentIndex - 1));
        setCurrentIndex(currentIndex - 1);

        if (currentIndex - 1 === 0) {
          setIsDeleting(false);
          setCurrentServiceIndex((prev) => (prev + 1) % capabilities.length);
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, isDeleting, currentServiceIndex]);

  return (
    <section className="relative overflow-hidden bg-bg-main py-14 sm:py-22 lg:py-18">
      {/* Decorative line-art circles — hidden on small phones to avoid clutter */}
      <svg
        className="pointer-events-none absolute inset-0 hidden h-full w-full sm:block"
        viewBox="0 0 1440 800"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
      >
        <circle cx="120" cy="60" r="180" stroke="#D9A566" strokeOpacity="0.4" strokeWidth="1.5" />
        <circle cx="1050" cy="230" r="230" stroke="#D9A566" strokeOpacity="0.3" strokeWidth="1.5" />
        <path
          d="M340 -40 C 420 120, 260 260, 380 400"
          stroke="#D9A566"
          strokeOpacity="0.4"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          d="M1250 60 C 1400 260, 1180 420, 1380 650"
          stroke="#D9A566"
          strokeOpacity="0.3"
          strokeWidth="1.5"
          fill="none"
        />
      </svg>

      <div className="relative mx-auto max-w-4xl px-4 sm:px-8 mt-10">
        {/*
          The title, subtitle, sub-heading, and description below used to
          be wrapped in <motion.div initial={{opacity:0}} animate={{opacity:1}}>
          blocks. That's exactly the text Lighthouse's LCP breakdown flagged
          as the page's Largest Contentful Paint element, and animating it
          in from opacity:0 (plus a 0.05-0.1s delay + 0.6s duration) meant
          the browser couldn't consider it "painted" until framer-motion's
          JS had loaded, mounted, and run ~700ms of animation — that's
          almost the entire 2,100ms "element render delay" the report
          measured for LCP. This content is above the fold on every load,
          so it now renders at full opacity immediately in plain <div>s;
          the fade-in is kept further down (stats/typed text) where it
          doesn't gate LCP. */}
        <div>
          <h1 className="text-3xl font-bold leading-[1.15] tracking-tight text-text-primary dark:text-white sm:text-4xl lg:text-5xl lg:leading-[1.1]">
            {t('hero.title')}
          </h1>

          <p className="mt-4 max-w-2xl text-base leading-7 text-text-secondary dark:text-slate-400 sm:mt-6 sm:text-lg sm:leading-8">
            {t('hero.subtitle')}
          </p>
        </div>

        {/* Sub Heading */}
        <div className="mt-6 sm:mt-8">
          <h2 className="text-xl font-bold leading-[1.15] tracking-tight text-text-primary dark:text-white sm:text-2xl lg:text-3xl">
            {t('hero.engineeringTitle')}
          </h2>
        </div>

        {/* Description — the LCP element; see note above */}
        <div className="mt-6 space-y-4 text-base leading-7 text-text-secondary dark:text-slate-400 sm:mt-8 sm:space-y-5 sm:text-lg sm:leading-8">
          <p>
            {t('hero.description')}
          </p>
        </div>

        {/* Stats */}
        <motion.div
          ref={statsRef}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mt-8 rounded-2xl border border-blue-100 bg-white/70 px-4 py-5 shadow-sm backdrop-blur-sm sm:mt-10 sm:px-6 sm:py-6"
        >
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 text-center sm:gap-x-6 sm:gap-y-8 md:grid-cols-4">
            {[
              { value: 30, label: t('hero.stats.clients') },
              { value: 10, label: t('hero.stats.experience') },
              { value: 20, label: t('hero.stats.team') },
              { value: 97, label: t('hero.stats.satisfaction'), isPercent: true },
            ].map((item, index) => (
              <div key={index}>
                <Counter target={item.value} isPercent={item.isPercent} shouldStart={statsInView} />
                <p className="text-xs text-slate-500 sm:text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Dynamic capability text — just below the counter banner */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
          className="mt-6 text-center sm:mt-8"
        >
          <p className="text-lg font-semibold text-text-primary sm:text-xl">
            {t('hero.poweredBy')}{' '}
            <span
              className={`bg-gradient-to-r ${gradients[currentServiceIndex]} bg-clip-text text-transparent`}
            >
              {typedText}
            </span>
            <span className="animate-pulse text-text-primary">|</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutUs;